<template>

  <div v-if="tour" class="serp-detail" itemscope="" itemtype="https://schema.org/Product">
    <div class="header-h1 serp-detail__h1">
      <h1>{{ tour.name }}</h1>
    </div>
    <tour-microdata v-if="tour.meta" :tour="tour" :meta="tour.meta" />
    <link
      v-if="sizeSm"
      rel="preload"
      :href="galleryFirstImgMobile"
      as="image">
    <link
      v-else
      rel="preload"
      :href="galleryFirstImgDesktop"
      as="image">
    <sticky-anchor-menu
      v-if="!sizeMdMax"
      :anchors="stickyAnchors"
      :sticky-trigger="$refs['serp-detail-main']"
    />

    <div class="serp-detail__container l-outer-width l-inner-padding">
      <div v-if="tour.days.length" class="serp-detail__sub-title">
        <tour-sub-title
          :reviews="reviews"
          :days="tour.days"
          :awards="getAwards"
          @show-reviews="$refs.reviews.showReviews()"
        />
      </div>
      <mounting-portal
        v-if="gallery.length"
        mount-to="#before-breadcrumbs"
        :disabled="!sizeSm"
        append
        slim
      >
        <div
          class="serp-detail__gallery"
          :class="{ 'l-inner-padding': sizeSm }"
        >
          <div class="serp-detail__gallery-wrapper">
            <tour-gallery-slider v-if="sizeSm" :gallery="gallery" />
            <tour-gallery-grid v-else :gallery="gallery" />
            <favourite-tour-button
              :id="tour.id"
              class="serp-detail__gallery-favourite"
            />
          </div>
        </div>
      </mounting-portal>
      <div ref="serp-detail-main" class="serp-detail__main">
        <div class="serp-detail__main-container u-flex-grow">
          <div
            class="serp-detail__section _steps"
            :class="{ _bordered: !sizeSm }"
          >
            <tour-booking-steps />
          </div>
          <BookingPopupMobile
            v-if="sizeMdMax"
            ref="booking-popup-mobile"
            :tour="tour"
            :offer-selected="isOfferSelected"
            :form-data = "formData"
            :offers="offers"
            :offer="offer"
            :is-show-min-price="isShowMinPrice"
            :is-all-offers-loaded="isAllOffersLoaded"
            :await-next-page="awaitNextPage"
            @change="changeBooking"
            @update:selectedOffer="updateSelectedOffer"
            @changeFormData="formData = $event"
            @scroll-end = loadNextOffersPage
          />

          <div id="tour-description" class="serp-detail__section _bordered"
               :class="{'_with-slider' : abtestOutOfPlaceNewView && this.emptyDates }">
            <tour-general-info :tour="tour" :offer="selectedOffer" @booking="triggerBookingSubmit"/>
            <tour-categories v-if="tour.types.length" :categories="tour.types" />


            <div v-if="sameCountry.length && abtestOutOfPlaceNewView && this.emptyDates"  class="serp-detail__bottom-slider slider-top" ref="sliderSimilar">
              <slider-tours :tours="sameCountry">
                <template #title>
                  <a
                    :href="linkSameCountry"
                    target="_blank"
                    class="serp-detail__section-title"
                  >
                    {{ $t("serpDetail.similar") }}
                  </a>
                </template>
              </slider-tours>
            </div>
            <div class="serp-detail__complaint _mobile">
              <tour-complaint :tourId="tour.id"/>
            </div>
          </div>

          <div v-if="tour.description" class="serp-detail__section _bordered">
            <extension-cropped
              :label-extended="$t('dropdownTrigger.description.extended')"
              :label-shrunk="$t('dropdownTrigger.description.shrunk')"
            >
              <div itemprop="description" v-html="tour.description"></div>
            </extension-cropped>
          </div>
          <!-- @todo
          <div class="serp-detail__section _bordered">
            <gallery-places
              :places="places"
            >
              <template #title>
                <div class="serp-detail__section-title">
                  {{ $t("serpDetail.mainImpressions") }}
                </div>
              </template>
            </gallery-places>
          </div> -->
          <div v-if="tour.video_link" class="serp-detail__section _bordered">
            <yt-youtube-video :src="tour.video_link" />
          </div>
          <div v-if="tour.tour_leader" class="serp-detail__section _bordered">
            <tour-organizer :tour="tour" />
          </div>
          <div
            v-if="tour.days.length"
            id="tour-route"
            class="serp-detail__section"
          >
            <tour-program :tour="tour" :offer-id="selectedOffer.id">
              <template #title>
                <h2 class="serp-detail__section-title">
                  {{ $t("serpDetail.program.title") }}
                </h2>
              </template>
            </tour-program>
          </div>
          <div
            v-if="tour.included || tour.not_included"
            id="tour-conditions"
            class="serp-detail__section"
          >
            <h2
              class="serp-detail__section-title"
              v-html="$t('serpDetail.conditions')"
            ></h2>
            <tour-conditions
              :included="tour.included"
              :not-included="tour.not_included"
            />
          </div>
          <div
            v-if="
              tour.hotel_name ||
              tour.type_allocation?.length ||
              tour.photo_allocation?.length
            "
            id="tour-allocation"
            class="serp-detail__section"
          >
            <h2 class="serp-detail__section-title">
              {{ $t("serpDetail.allocation") }}
            </h2>
            <tour-allocation :tour="tour" />
          </div>
          <div
            v-if="accommodation.length"
            id="tour-accommodation"
            class="serp-detail__section"
          >
            <h2 class="serp-detail__section-title">
              {{ $t("serpDetail.accommodation") }}
            </h2>
            <tour-accommodation :accommodation="accommodation" />
          </div>
          <div
            v-if="services.length"
            id="tour-services"
            class="serp-detail__section"
          >
            <h2 class="serp-detail__section-title">
              {{ $t("serpDetail.services") }}
            </h2>
            <tour-services :services="services" />
          </div>
          <div id="tour-important" class="serp-detail__section">
            <h2 class="serp-detail__section-title">
              {{ $t("serpDetail.important.title") }}
            </h2>
            <tour-important
              :tour="tour"
              @book="triggerBookingSubmit"
              @ask="triggerAskHost"
            />
          </div>
          <div v-if="selectedOffer.id" class="serp-detail__section">
            <h2
              class="serp-detail__section-title"
              v-html="$t('serpDetail.arrival.title')"
            ></h2>
            <tour-arrival :offer="selectedOffer" />
          </div>
          <div
            v-if="reviews.reviews && reviews.reviews.length"
            id="tour-reviews"
            class="serp-detail__section"
          >
            <tour-reviews
              ref="reviews"
              :preview="reviews"
              :tour-id="tour.id"
              :item-name="tour.name"
              :expert-data="tour.expert_data"
            />
          </div>
        </div>
        <aside class="serp-detail__aside u-flex-shrink-0"
               ref="aside">
          <div class="serp-detail__booking">
            <booking-popup
              v-show="(sizeMdMax && isShowBookingBopupBar) || !sizeMdMax"
              ref="booking-popup"
              :tour="tour"
              :offer-selected="isOfferSelected"
              :form-data = "formData"
              :offers="offers"
              :offer="offer"
              :is-show-min-price="isShowMinPrice"
              :is-all-offers-loaded="isAllOffersLoaded"
              :await-next-page="awaitNextPage"
              @change="changeBooking"
              @update:selectedOffer="updateSelectedOffer"
              @changeFormData="formData = $event"
              @scroll-end = loadNextOffersPage
            />
          </div>
          <div class="serp-detail__host">
            <tour-host
              ref="tour-host"
              :expert-data="tour.expert_data"
              :trip-id="tour.id"
              :awards="getAwards"
            />
          </div>
          <div class="serp-detail__complaint">
            <tour-complaint :tourId="tour.id"/>
          </div>
        </aside>
      </div>
      <preloader :visible="pending" position="fixed" overlay />
    </div>

    <div
      v-if="sameCountry.length || sameType.length|| latestOrSpecial.length"
      class="serp-detail__bottom"
    >
      <div class="serp-detail__bottom-container l-outer-width l-inner-padding">
        <div v-if="sameCountry.length && !(abtestOutOfPlaceNewView && this.emptyDates)" class="serp-detail__bottom-slider">
          <slider-tours :tours="sameCountry">
            <template #title>
              <a
                :href="linkSameCountry"
                target="_blank"
                class="serp-detail__section-title"
              >
                {{ getCountryName }}
              </a>
            </template>
          </slider-tours>
        </div>
        <div v-if="sameType.length" class="serp-detail__bottom-slider">
          <slider-tours :tours="sameType">
            <template #title>
              <a
                :href="linkSameType"
                target="_blank"
                class="serp-detail__section-title"
              >
                {{ getMainTypeName }}
              </a>
            </template>
          </slider-tours>
        </div>
        <div v-if="latestOrSpecial.length" class="serp-detail__bottom-slider">
          <slider-tours :tours="latestOrSpecial">
            <template #title>
              <div
                v-if="latestToursIds.length"
                class="serp-detail__section-title"
                v-html="$t('serpDetail.recentlyViewed')"
              />
              <a
                v-else
                :href="linkLatestOrSpecial"
                target="_blank"
                class="serp-detail__section-title"
                v-html="$t('serpDetail.special')"
              />
            </template>
          </slider-tours>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="serp-detail" ><div>...</div><preloader position="fixed" overlay /></div>
</template>

<script>
import qs from "qs";
import { axiosPlaces } from "@/services/scopes/axios-places";
import {mapMutations, mapState} from "vuex";
import { i18n } from "@/plugins/i18n";
import { useSerp } from "@/state/serp";
import TourMicrodata from "@/pages/serp-detail/components/TourMicrodata.vue";
import StickyAnchorMenu from "@/components/ui-kit/sticky-anchor-menu/index.vue";
import TourSubTitle from "@/pages/serp-detail/components/TourSubTitle.vue";
import TourGalleryGrid from "@/pages/serp-detail/components/TourGalleryGrid.vue";
import TourGallerySlider from "@/pages/serp-detail/components/TourGallerySlider.vue";
import FavouriteTourButton from "@/components/ui-kit/favourite-tour-button/index.vue";
import TourBookingSteps from "@/pages/serp-detail/components/TourBookingSteps.vue";
import TourCategories from "@/pages/serp-detail/components/TourCategories.vue";
import TourGeneralInfo from "@/pages/serp-detail/components/TourGeneralInfo.vue";
import TourProgram from "@/pages/serp-detail/components/TourProgram.vue";
import TourComplaint from "@/pages/serp-detail/components/TourComplaint";
// @todo import GalleryPlaces from "@/components/organisms/gallery-places/index.vue";
import YtYoutubeVideo from "@/components/ui-kit/yt-youtube-video/index.vue";
import TourOrganizer from "@/pages/serp-detail/components/tour-organizer/index.vue";
import TourConditions from "@/pages/serp-detail/components/TourConditions.vue";
import TourAllocation from "@/pages/serp-detail/components/TourAllocation.vue";
import TourAccommodation from "@/pages/serp-detail/components/TourAccommodation.vue";
import TourServices from "@/pages/serp-detail/components/TourServices.vue";
import TourImportant from "@/pages/serp-detail/components/TourImportant.vue";
import TourArrival from "@/pages/serp-detail/components/TourArrival.vue";
import TourReviews from "@/pages/serp-detail/components/tour-reviews/index.vue";
import ExtensionCropped from "@/components/ui-kit/extension-cropped/index.vue";
import BookingPopup from "@/components/organisms/booking-popup/index.vue";
import BookingPopupMobile from "@/components/organisms/booking-popup-mobile/index.vue";
import TourHost from "@/pages/serp-detail/components/TourHost.vue";
import SliderTours from "@/components/organisms/slider-tours/index.vue";
import Preloader from "@/components/ui-kit/preloader/index.vue";
import { sizeSm, sizeMdMax } from "@/assets/js/utils/mq";
import { isObjectValuesEmpty } from "@/assets/js/utils/is-object-values-empty";
import {
  getLatestToursIds,
  updateLatestToursIds,
} from "@/assets/js/utils/latestTours";
import {
  getBookingOffers,
  getOfferFromLocalStorage,
} from "@/assets/js/utils/bookingOffers";
import {abTestController} from "@/plugins/abtest";
import {axiosOffers} from "@/services/scopes/axios-offers";
import {ytEvents} from "@/plugins/analytics";
export default {
  name: "serp-detail",
  components: {
    TourMicrodata,
    StickyAnchorMenu,
    TourSubTitle,
    TourGalleryGrid,
    TourGallerySlider,
    FavouriteTourButton,
    TourBookingSteps,
    TourCategories,
    TourGeneralInfo,
    TourProgram,
    // @todo GalleryPlaces,
    YtYoutubeVideo,
    TourOrganizer,
    TourConditions,
    TourAllocation,
    TourAccommodation,
    TourServices,
    TourImportant,
    TourArrival,
    TourReviews,
    ExtensionCropped,
    BookingPopup,
    BookingPopupMobile,
    TourHost,
    SliderTours,
    Preloader,
    TourComplaint
  },
  data() {
    return {
      places: [],
      similar: [],
      sameCountry: [],
      sameType: [],
      latestOrSpecial: [],
      latestToursIds: [],
      selectedOffer: {},
      pending: false,
      isRussian: i18n.locale === "ru",
      isOfferSelected: false,
      isShowBookingBopupBar: false,
      formData: {
        offer: {},
        participants: 1,
      },
      offers: {},
      offer: {},
      awaitOffer: true,
      limit: 20,
      offset: 0,
      total:0,
      isAllOffersLoaded: false,
      awaitNextPage: false,
      toursWithAwards: [
        25392,
        29738,
        11073,
        23607,
        17952,
        17952,
        7129,
        25127,
        10557,
        28022
      ]
    };
  },
  computed: {

    ...mapState("serpDetail", {
      ssrOffers: ({ offers }) => offers,
      //meta: ({ meta }) => meta,
    }),
    ...mapState("currency", ["currency"]),
    tourId () {
      return this.$route.params['id'];
    },
    tour () {
      return this.$store.state.tours[this.tourId];
    },
    reviews () {
      return this.$store.state.reviews[this.tourId];
    },
    getAwards() {
      if (this.toursWithAwards.indexOf(parseInt(this.tourId) ) != -1) {
        return {
          code: 'best_tour',
          name: this.$t("serpDetail.awards.bestTours"),
          icon: 'crown',
        }
      }

      if (this.tour.expert_id === 9103) {
        return {
          code: 'best_expert',
          name: this.$t("serpDetail.awards.bestExpert"),
        }
      }

      return false;
    },
    stickyAnchors() {
      return [
        {
          name: this.$t("serpDetail.stickyAnchors.description"),
          selector: "#tour-description",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.route"),
          selector: "#tour-route",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.conditions"),
          selector: "#tour-conditions",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.allocation"),
          selector: "#tour-allocation",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.services"),
          selector: "#tour-services",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.important"),
          selector: "#tour-important",
        },
        {
          name: this.$t("serpDetail.stickyAnchors.reviews"),
          selector: "#tour-reviews",
        },
      ];
    },
    galleryFirstImgMobile() {
      return this.tour.gallery[0]?.MEDIA_BIG;
    },
    galleryFirstImgDesktop() {
      return this.tour.gallery[0]?.MEDIA_BIG;
    },
    gallery() {
      return this.tour.gallery;
    },
    services() {
      return Object.values(this.tour.additional_services || {});
    },
    accommodation() {
      const accommodation = this.tour.type_accommodation;
      return Array.isArray(accommodation)
        ? accommodation
        : isObjectValuesEmpty(accommodation)
          ? []
          : [accommodation];
    },
    queryParamsCommon() {
      if (this.isShowRuVersion()) {
        i18n.locale = 'ru';
      }
      return {
        lang: i18n.locale,
        currency: this.currency.id.toLowerCase(),
        take: 16,
        is_period_strict: 1,
        sort_by: "rank",
        sort_dir: "desc",
      };
    },
    querySimilar() {
      const {
        tour,
        selectedOffer: { dateFrom, dateTo },
        queryParamsCommon,
      } = this;

      if (!tour || !dateFrom || !dateTo) {
        return "";
      }

      return `${qs.stringify(
        {
          ...queryParamsCommon,
          types: Object.values(tour.type_id),
        },
        { arrayFormat: "brackets" }
      )}&${qs.stringify(
        {
          loc: tour.country_id.map((id) => ({ type: "country", id })),
          period: [{ from: dateFrom, to: dateTo }],
        },
        { arrayFormat: "indices" }
      )}`;
    },
    linkSimilar() {
      return `/tours?${this.querySimilar}`;
    },
    queryLatestOrSpecial() {
      const {
        selectedOffer: { dateFrom, dateTo },
        latestToursIds,
        queryParamsCommon,
      } = this;

      if (latestToursIds.length) {
        return qs.stringify(
          {
            ...queryParamsCommon,
            ids: latestToursIds,
          },
          { arrayFormat: "brackets" }
        );
      }

      if (!dateFrom || !dateTo) {
        return "";
      }

      return qs.stringify(
        {
          ...queryParamsCommon,
          have_discount: 1,
          period: [{ from: dateFrom, to: dateTo }],
        },
        { arrayFormat: "indices" }
      );
    },
    linkLatestOrSpecial() {
      const { latestToursIds } = this;

      if (latestToursIds.length) {
        return `/tours?${this.queryLatestOrSpecial}`;
      }
      return '/tours/specials/sale';
    },
    emptyDates() {
      const {
        selectedOffer: { dateFrom, dateTo },
      } = this;
      if(!dateFrom || !dateTo) {
        return  true;
      }
      return false;
    },
    querySameCountry() {
      const {
        tour,
        selectedOffer: { dateFrom, dateTo },
        queryParamsCommon,
      } = this;

      if (!tour) {
        return "";
      }


      let take = {};
      if(this.abtestOutOfPlaceNewView && this.emptyDates) {
        take = {take: 4}
      }
      return `${qs.stringify(
        {
          ...queryParamsCommon,
          ...take,
          excluded_tours: [tour.id]
        },
        { arrayFormat: "brackets" }
      )}&${qs.stringify(
        {
          loc: [{ type: "country", id: tour.country_id[0] }],//tour.country_id.map((id) => ({ type: "country", id })),
          period: [{ from: dateFrom, to: dateTo }],
        },
        { arrayFormat: "indices" }
      )}`;
    },
    linkSameCountry() {
      const countryCode = this.tour.country_code || (this.tour.country ? this.tour.country.toLowerCase() : "");
      return countryCode ? `/tours/country/${countryCode}` : "/tours";
    },
    querySameType() {
      const {
        tour,
        selectedOffer: { dateFrom, dateTo },
        queryParamsCommon,
      } = this;

      if (!tour || !dateFrom || !dateTo) {
        return "";
      }

      return `${qs.stringify(
        {
          ...queryParamsCommon,
          types: [tour.type_main],
          excluded_tours: [tour.id]
        },
        { arrayFormat: "brackets" }
      )}&${qs.stringify(
        {
          period: [{ from: dateFrom, to: dateTo }],
        },
        { arrayFormat: "indices" }
      )}`;
    },
    linkSameType() {
      return `/tours/type/${this.getMainTypeCode}`;
    },
    getCountryName() {
      const {tour} = this;
      if(!tour) return;
      return tour.country ? this.$t("serpDetail.sameCountry")+" "+tour.country.split(',')[0].trim() : "";
    },
    getMainTypeCode() {
      const {tour} = this;
      if(!tour) return;
      let mainType = tour.types.filter((type) => {
        if(type.id == tour.type_main) {
          return type;
        }
      })
      return mainType.length ? mainType[0].code : "";
    },
    getMainTypeName() {
      const {tour} = this;
      if(!tour) return;

      let mainType = tour.types.filter((type) => {
        if(type.id == tour.type_main) {
          return type;
        }
      })

      return mainType.length ? this.$t("serpDetail.sameType")+" "+mainType[0].name : "";
    },
    sizeSm,
    sizeMdMax,
    abtestOutOfPlaceNewView () {
      return abTestController.getSegment(16763) === "B";
    },
    isShowMinPrice() {
      return abTestController.getSegment(17178) === 'B' && this.isRussian;
    },

  },
  created() {
    if (vueOptions.ssr) {
      return;
    }

    updateLatestToursIds(this.tourId);
    this.latestToursIds = getLatestToursIds(this.tourId);

    this.fetchPlaces();

  },
  watch: {
    // querySimilar() {
    //   this.fetchSimilar();
    // },
    "tour.id": {
      deep: true,
      handler() {
        if(!vueOptions.ssr && this.awaitOffer) {
          this.getOffers();
        }

      },
    },

    "formData.offer": {
      deep: true,
      handler({ custom }) {
        if (custom) {
          this.offer = this.formData.offer;
          return;
        }

        this.getDataOfOffer();
      },
    },
    queryLatestOrSpecial() {
      this.fetchLatestOrSpecial();
    },
    querySameCountry() {
      this.fetchSameCountry()
    },
    querySameType() {
      this.fetchSameType()
    },
  },

  serverPrefetch () {
    return Promise.allSettled([
      this.fetchReviews(),
      this.fetchTour()
    ]);
  },
  mounted() {

    fakeWindow.addEventListener("Auth::data", (e) => {
      if (e.detail.user.isLoggedIn) {
        this.setUser(e.detail.user);
      }
    });

    fakeWindow.addEventListener("Auth::user", (e) => {
      if (e.detail) {
        this.setUser(e.detail);
      }
    });

    fakeWindow.addEventListener("header::show", (e) => {
      this.showHeader = e.detail;
    });

    fakeWindow.addEventListener("scroll", () => {

      if(!this.$refs.sliderSimilar) {
        return ;
      }
      let offTop =  this.$refs.sliderSimilar?.clientHeight + this.$refs.sliderSimilar?.offsetTop;
      if(this.abtestOutOfPlaceNewView  &&  this.emptyDates) {
        if(fakeWindow.scrollY < offTop) {
          this.$refs.aside.removeAttribute('style');
          this.$refs.aside.setAttribute("style", "position: relative!important;");
        }
        else {
          this.$refs.aside.removeAttribute('style');
          this.$refs.aside.setAttribute("style", "position: sticky!important;")
        }
      }
    });
    fakeWindow.addEventListener("scroll",() => {
      this.isShowBookingBopupBar = fakeWindow.scrollY > (this.$refs["booking-popup-mobile"]?.$el?.offsetTop + this.$refs["booking-popup-mobile"]?.$el?.scrollHeight)
    })
    if(!this.tour){
      this.fetchTour();
    }

    if(!this.reviews){
      this.fetchReviews();
    }
    if(this.emptyDates && this.abtestOutOfPlaceNewView) {
      this.fetchSameCountry();
    }
    this.isOfferSelected = this.isShowMinPrice ? false : true;
    if (!vueOptions.ssr) {
      this.getOffers();
    }
  },

  methods: {
    ...mapMutations({
      setUser: "user/setUser",
    }),
    getBookingOffers,
    async getDataOfOffer() {
      await axiosOffers
        .getPublicOffer(this.tourId, this.formData.offer.id)

        .then(
          ({
             data: {
               data: [offer],
             },
           }) => {
            this.offer = {
              ...offer,
              period_date_days: offer.period_date_days,
            };
          }
        )
        .catch(console.log.bind(console));
    },
    async getOffers() {
      this.awaitOffer = true;
      if(!this.tour) {
        return
      }
      await this.getBookingOffers(this.tour, this.offset, this.limit)
        .finally(() => {
          this.awaitOffer = false;
        })
        .then((data) => {
          this.total = data.total
          this.offers = data.mappedOffers;
          this.offset += data.mappedOffers.length;
          this.isAllOffersLoaded = this.offers.length === this.total
          if(this.isShowMinPrice) {
            this.getMinPriceOffer()
          }
          else {
            this.formData.offer = getOfferFromLocalStorage(this.offers);
          }
          this.formData.participants = this.getParticipantsCount();
      });
    },
    async getMinPriceOffer() {
      if(!this.tour) {
        return
      }
      await axiosOffers.getMinPriceOffer(this.tourId)
        .then(({data}) => {
          if(data.success && data.data) {
            this.formData.offer = data.data
          } else {
            this.formData.offer = getOfferFromLocalStorage(this.offers);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async loadNextOffersPage() {

      if(!this.tour || this.awaitNextPage || this.isAllOffersLoaded) {
        return
      }

      this.awaitNextPage = true;
      await this.getBookingOffers(this.tour, this.offset, this.limit)
        .finally(() => {

        })
        .then((data) => {
          const mappedOffers = data.mappedOffers;
          this.offers = [...this.offers, ...mappedOffers];

          this.isAllOffersLoaded = (this.offset + mappedOffers.length) >= this.total || !mappedOffers;
          this.offset += mappedOffers.length;
          this.awaitNextPage = false;
        });

    },
    getParticipantsCount() {
      const participants = localStorage.getItem('participants');

      return participants === null || isNaN(+participants) ? this.formData.participants : +participants;
    },
    updateSelectedOffer(event) {
      this.formData = event.formData
      this.isOfferSelected = event
    },
    fetchTour() {
      if (this.isShowRuVersion()) {
        i18n.locale = 'ru';
      }
      // return the Promise from the action
      return this.$store.dispatch('fetchTour', {
        id: this.tourId,
        currency: this.currency.id.toLowerCase()
      });
    },
    changeBooking(event) {
      this.selectedOffer = event;
    },
    fetchReviews() {
      // return the Promise from the action
      return this.$store.dispatch('fetchReviews', this.tourId);
    },

    async fetchPlaces() {
      this.pending = true;

      axiosPlaces
        .getPlaces(this.tourId)
        .finally(() => {
          this.pending = false;
        })
        .then(
          ({
            data: {
              data: { places },
            },
          }) => {
            this.places = places || [];
          }
        )
        .catch(() => {
          this.places = [];
        });
    },
    async fetchSimilar() {
      const { getToursSearch } = useSerp();
      const { querySimilar } = this;
      let similar = [];

      if (!querySimilar) {
        return;
      }

      try {
        const { items } = await getToursSearch(querySimilar);

        similar = items;
      } catch (err) {
        similar = [];
      }

      if (this.querySimilar === querySimilar) {
        this.similar = similar;
      }
    },
    async fetchLatestOrSpecial() {
      const { getToursSearch } = useSerp();
      const { queryLatestOrSpecial } = this;
      let latestOrSpecial = [];

      if (!queryLatestOrSpecial) {
        return;
      }

      try {
        const { items } = await getToursSearch(queryLatestOrSpecial);

        latestOrSpecial = items;
      } catch (err) {
        latestOrSpecial = [];
      }

      if (this.queryLatestOrSpecial === queryLatestOrSpecial) {
        this.latestOrSpecial = latestOrSpecial;
      }
    },
    async fetchSameCountry() {
      const { getToursSearch } = useSerp();
      const { querySameCountry} = this;
      let sameCountry = [];

      if (!querySameCountry) {
        return;
      }

      try {
        const { items } = await getToursSearch(querySameCountry);
        sameCountry = items;
      } catch (err) {
        sameCountry = [];
      }

      if (this.querySameCountry === querySameCountry) {
        this.sameCountry = sameCountry;
      }
    },
    async fetchSameType(){
      const { getToursSearch } = useSerp();
      const { querySameType} = this;
      let sameType = [];

      if (!querySameType) {
        return;
      }

      try {
        const { items } = await getToursSearch(querySameType);

        sameType = items;
      } catch (err) {
        sameType = [];
      }

      if (this.querySameType === querySameType) {
        this.sameType = sameType;
      }
    },
    clickAskButton() {
      this.$refs["tour-host"].popups.ask = true;
      ytEvents.track("Tour detail" , "Click Message to expert button",);
      if (typeof window.youtravel.DataCollector !== "undefined") {
        window.youtravel.DataCollector.trackEvent("Tour detail", "Click Message to expert button", {
          'tour_id': this.tourId || "",
          'list': "",
          'filter': {},
          'additional': {},
          'meta': {}
        })
      }
    },
    triggerBookingSubmit(event) {
      setTimeout(() => this.$refs["booking-popup"].onSubmit(event.trackingLabel));
    },
    triggerAskHost() {
      setTimeout(this.clickAskButton);
    },
    isShowRuVersion() {
      const alwaysRuVersionTours = [
        38376, 41461, 30710, 33536,
        35027, 41183, 38483, 39763,
        35920, 21562, 26540, 28195,
        39427, 10557, 40975, 31907,
        39074, 10796, 39895, 31539,
      ];

      return alwaysRuVersionTours.includes(parseInt(this.tourId));
    }
  },
  beforeDestroy() {
    fakeWindow.removeEventListener("scroll");
  },
};
</script>

<style lang="scss">
@use "@/assets/scss/global.scss";
@use "@/assets/scss/font-sf-pro.scss";
@use "@/assets/scss/libraries/elements/date-selector-new.scss";

$p: ".tour-detail";
$b: ".serp-detail";

@include mixins.header();

* {
  font-family: var.$font-family-sf-pro-text;
}

#{$p} {
  @include mixin.size-md-min {
    .header-h1 {
      > h1 {
        font-size: 40px;
        max-width: percentage(func.divide(776, 1176));
      }
    }
  }
}
@include mixin.size-sm-only() {
  #before-breadcrumbs {
    height: 408px;
  }
}
@include mixin.size-ssm() {
  #before-breadcrumbs {
    height: 256px;
  }
}
@include mixin.size-sm {
  #before-breadcrumbs {
    background: #eeeef2;
    width: 100%;
  }
  .serp-detail__container .serp-detail__gallery {
    display: none;
  }
}


#serp-detail {
  width: 100%;
}
#{$b} {
  width: 100%;

  &__h1 {
    width: 100%;
    max-width: 1256px;
    padding: 0 16px;
    &>h1 {
      font-size: 28px;
    }
  }

  @include mixin.size-md {
    overflow: hidden;
    &__h1 {
      margin: 0 auto 16px;
    }
  }

  @include mixin.size-md-min {
    &__h1 {
      margin: 0 auto 12px;
      padding: 0 40px;
      &>h1 {
        font-size: 48px;
        line-height: 115%;
      }
    }
  }

  &__sub-title {
    margin-bottom: 28px;
  }

  &__gallery {
    margin-bottom: 10px;

    @include mixin.size-sm {
      max-width: 100vw;
    }

    @include mixin.size-md-min {
      margin-bottom: 51px;
    }

    @include mixin.size-xl-min {
      margin-bottom: 64px;
    }

    &-wrapper {
      position: relative;
    }

    &-favourite {
      position: absolute;
      top: var.$inner-pd;
      right: 0;
      z-index: 1;

      @include mixin.size-md-min {
        right: var.$inner-pd;
      }
    }
  }

  &__main {
    display: flex;
    align-items: flex-start;

    &-container {
      @include mixin.size-lg-min {
        margin-right: 32px;
      }

      @include mixin.size-xl-min {
        margin-right: 124px;
      }
    }
  }

  &__section {
    margin-bottom: 56px;

    @include mixin.size-md-only {
      margin-bottom: 64px;
    }

    @include mixin.size-xl-min {
      margin-bottom: 64px;
    }

    &._bordered {
      padding-bottom: 40px;
      margin-bottom: 40px;
      border-bottom: 1px solid var.$gray-light;

      @include mixin.size-md-only {
        padding-bottom: 56px;
        margin-bottom: 56px;
      }

      @include mixin.size-xl-min {
        padding-bottom: 56px;
        margin-bottom: 56px;
      }
    }
    &._with-slider {
      padding-bottom: 0!important;
    }

    &._steps._bordered {
      @include mixin.size-md-min {
        padding-bottom: 18px;
      }
    }

    &-title {
      font-family: var.$font-family-sf-pro-display;
      font-weight: 700;
      font-size: 24px;
      line-height: 115%;
      color: var.$black;
      margin-bottom: 24px;
      padding: 0px;

      @include mixin.size-md-min {
        margin-bottom: 32px;
      }

      #{$b}__bottom-slider & {
        margin-bottom: 0;
      }
    }
  }

  &__aside {
    @include mixin.size-lg-min {
      position: sticky;
      top: 72px;
      z-index: 1;
      width: 400px;
      padding-bottom: 64px;
    }
  }

  &__host {
    display: none;
    margin-top: 32px;

    @include mixin.size-lg-min {
      display: block;
    }
  }
  &__complaint{

    display: none;
    @media (min-width: var.$size-lg) {
      margin-top: 24px;
      display: block;
    }
  }

  &__bottom {
    background: var.$gray-light;
    padding: 48px 0 8px;
    overflow: hidden;

    @include mixin.size-lg-min {
      padding-top: 64px;
    }

    &-slider {
      padding-bottom: 48px;

      @include mixin.size-lg-min {
        padding-bottom: 64px;
      }
    }
  }
}
.tour-program {

  &__block {
    background: #fff;
    border: 1px solid #eeeef2;
    box-shadow: 2px 2px 15px rgb(153 153 169 / 15%);
    border-radius: 12px;
    padding: 16px 20px 24px;
    margin-top: 24px;

    &-title,
    &-description {
      color: #828296;
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 3px;
    }

    &-value {
      color: #000;
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 7px;
      display: flex;
      align-items: center;

      span {
        margin-right: 8px;
      }
    }

    .g-tile {
      background: #F6F7FA;
      border: 1px solid #EEEEF2;
    }
  }
}
.slider-top {
  padding-top: 40px;
  padding-bottom: 48px!important;
}
._mobile {
  display: block;
  margin-top: 40px;
  @media (min-width: var.$size-lg) {
    display: none;
  }
}

</style>
